import type { Members } from "@/types/workspace";

/**
 * Sentinel person entry storable only on datasource TEMPLATE pages: "assign
 * whoever creates a page from this template". Resolved to the creating user
 * when a row is instantiated — client-side in buildTemplateRowValue and
 * server-side in the instantiate endpoint. The id must stay in sync with
 * TEMPLATE_DUPLICATOR_USER_ID in the backend's datasourceTemplateService.
 */
export const TEMPLATE_DUPLICATOR_USER_ID = "__template_duplicator__";
export const TEMPLATE_DUPLICATOR_LABEL = "Person who duplicated page";

export interface DuplicatorUser {
  userId: string;
  userName: string;
  userEmail: string;
  image?: string;
}

export function isTemplateDuplicator(entry: unknown): boolean {
  return (
    typeof entry === "object" &&
    entry !== null &&
    (entry as { userId?: unknown }).userId === TEMPLATE_DUPLICATOR_USER_ID
  );
}

export function templateDuplicatorMember(): Members {
  return {
    userId: TEMPLATE_DUPLICATOR_USER_ID,
    userName: TEMPLATE_DUPLICATOR_LABEL,
    userEmail: "",
    role: "",
    joinedAt: "",
  };
}

export function currentUserAsMember(
  user: { id?: string; email: string; name?: string; image?: string } | null | undefined,
  workspaceMembers?: Members[],
): DuplicatorUser | null {
  if (!user?.email) return null;
  const member = workspaceMembers?.find((m) => m.userEmail === user.email);
  const image = member?.image || user.image;
  return {
    userId: member?.userId || user.id || "",
    userName: member?.userName || user.name || user.email,
    userEmail: user.email,
    ...(image ? { image } : {}),
  };
}

/**
 * Replace sentinel entries in person property values with the creating user.
 * Mutates databaseProperties (callers pass a fresh clone). With no resolvable
 * user the sentinel is dropped so it can never persist on a real row.
 */
export function resolveTemplateDuplicator(
  databaseProperties: Record<string, unknown> | null | undefined,
  currentUser: DuplicatorUser | null,
): void {
  if (!databaseProperties) return;
  for (const key of Object.keys(databaseProperties)) {
    const value = databaseProperties[key];
    if (!Array.isArray(value) || !value.some(isTemplateDuplicator)) continue;
    const others = value.filter((entry) => !isTemplateDuplicator(entry));
    const alreadyAssigned =
      !!currentUser &&
      others.some((entry) => {
        if (!entry || typeof entry !== "object") return false;
        const { userId, userEmail } = entry as { userId?: string; userEmail?: string };
        return (
          (!!userId && userId === currentUser.userId) ||
          (!!userEmail && userEmail === currentUser.userEmail)
        );
      });
    databaseProperties[key] =
      currentUser && !alreadyAssigned ? [...others, { ...currentUser }] : others;
  }
}
